import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import useAuthState from '@/states/auth.state';
import useTenantState from '@/states/tenant.state';
import {
  buildDraftKey,
  readDraft,
  removeDraft,
  writeDraft,
  type DraftEnvelope,
  type DraftStorageType,
} from '@/utils/lib/draftStorage';
import { useOnlineStatus } from './useOnlineStatus';

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'offline' | 'error';

export interface UseAutosaveOptions<T extends FieldValues> {
  /** The react-hook-form instance to watch. */
  form: UseFormReturn<T>;
  /** Stable namespace for this form, e.g. 'vision'. */
  formKey: string;
  /**
   * Bump when the form's field shape changes. Drafts written under an older
   * version are discarded instead of restored into fields that moved.
   */
  version?: number;
  storage?: DraftStorageType;
  /**
   * Autosave only runs while true. Callers gate on things like "server data has
   * loaded" and "user can edit", so a draft is never written from the empty
   * defaults that exist before the first reset().
   */
  enabled?: boolean;
  /** Distinguishes drafts of the same form bound to different records. */
  entityId?: number | string | null;
  /** Field names excluded from the draft (server-owned flags, transient UI). */
  exclude?: (keyof T | string)[];
  debounceMs?: number;
}

export interface UseAutosaveResult<T extends FieldValues> {
  status: AutosaveStatus;
  lastSavedAt: number | null;
  /**
   * A draft found on mount that differs from what the form was reset to.
   * Null when there is nothing worth offering to restore.
   */
  pendingDraft: DraftEnvelope<Partial<T>> | null;
  /** Applies `pendingDraft` to the form and dismisses the banner. */
  restoreDraft: () => void;
  /** Deletes the stored draft and dismisses the banner. */
  discardDraft: () => void;
  /** Deletes the stored draft without touching form values. For save success. */
  clearDraft: () => void;
  /** Writes the current values immediately, bypassing the debounce. */
  flush: (opts?: { pendingSync?: boolean }) => void;
  isOnline: boolean;
  /** True when the connection dropped and returned while this form was open. */
  wasOffline: boolean;
  acknowledgeReconnect: () => void;
}

function omitKeys<T extends Record<string, unknown>>(values: T, exclude: string[]): Partial<T> {
  if (exclude.length === 0) return values;
  const next: Record<string, unknown> = {};
  Object.keys(values).forEach(key => {
    if (!exclude.includes(key)) next[key] = values[key];
  });
  return next as Partial<T>;
}

/** True when every value is empty — an all-blank draft is not worth offering. */
function isBlank(values: Record<string, unknown>): boolean {
  return Object.values(values).every(value => {
    if (value === null || value === undefined || value === '' || value === false) return true;
    if (Array.isArray(value))
      return value.length === 0 || value.every(item => isBlank(item as Record<string, unknown>));
    if (typeof value === 'object') return isBlank(value as Record<string, unknown>);
    return false;
  });
}

/**
 * Persists form input to browser storage as the user types, so navigating away
 * or losing connectivity does not lose the work.
 *
 * Restoring is deliberately opt-in: every page in this app resets the form from
 * server data in an effect, and silently replaying a stale draft over fresher
 * server values would corrupt data with no visible cause. The hook surfaces
 * `pendingDraft` and lets the user choose.
 */
export function useAutosave<T extends FieldValues>({
  form,
  formKey,
  version = 1,
  storage = 'local',
  enabled = true,
  entityId,
  exclude = [],
  debounceMs = 800,
}: UseAutosaveOptions<T>): UseAutosaveResult<T> {
  const { isOnline, wasOffline, acknowledgeReconnect } = useOnlineStatus();
  const userId = useAuthState(state => state.userProfile?.id);
  const currentTenant = useTenantState(state => state.currentTenant);
  const tenantId = currentTenant?.id;

  const [status, setStatus] = useState<AutosaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [pendingDraft, setPendingDraft] = useState<DraftEnvelope<Partial<T>> | null>(null);

  const excludeList = useMemo(() => exclude.map(String), [exclude]);
  const storageKey = useMemo(
    () => buildDraftKey({ formKey, userId, tenantId, entityId }),
    [formKey, userId, tenantId, entityId],
  );

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Latest values mirrored into a ref so flush handlers bound to unload events
  // do not need to re-subscribe on every keystroke.
  const latestValuesRef = useRef<Partial<T> | null>(null);
  const isOnlineRef = useRef(isOnline);
  const enabledRef = useRef(enabled);
  // Guards the one-shot draft lookup so a re-render never re-opens a banner the
  // user already dismissed.
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    isOnlineRef.current = isOnline;
  }, [isOnline]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  const persist = useCallback(
    (values: Partial<T>, pendingSync: boolean) => {
      const envelope = writeDraft(storageKey, values, { version, storage, pendingSync });
      if (envelope) {
        setLastSavedAt(envelope.savedAt);
        setStatus(pendingSync ? 'offline' : 'saved');
      } else {
        // Storage unavailable or full. Surfaced through the indicator so the
        // user knows their work is not being held.
        setStatus('error');
      }
    },
    [storageKey, version, storage],
  );

  const flush = useCallback(
    (opts?: { pendingSync?: boolean }) => {
      if (!enabledRef.current) return;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      const values = latestValuesRef.current;
      if (!values) return;
      persist(values, opts?.pendingSync ?? !isOnlineRef.current);
    },
    [persist],
  );

  // One-shot draft lookup, run once the caller reports it is ready (server data
  // loaded). Compares against current form values so a draft identical to what
  // is already on screen never prompts.
  useEffect(() => {
    if (!enabled || hasCheckedRef.current) return;
    hasCheckedRef.current = true;

    const stored = readDraft<Partial<T>>(storageKey, { version, storage });
    if (!stored) return;

    const storedValues = stored.values as Record<string, unknown>;
    if (!storedValues || isBlank(storedValues)) {
      removeDraft(storageKey, storage);
      return;
    }

    const current = omitKeys(form.getValues() as Record<string, unknown>, excludeList);
    if (JSON.stringify(current) === JSON.stringify(storedValues)) {
      // Draft matches the loaded server state; nothing to offer.
      removeDraft(storageKey, storage);
      return;
    }

    setPendingDraft(stored);
    if (stored.pendingSync) setStatus('offline');
    setLastSavedAt(stored.savedAt);
  }, [enabled, storageKey, version, storage, form, excludeList]);

  // The change handler lives in a ref so the subscription effect below can
  // depend only on `enabled`. Re-subscribing on every render would tear down
  // the pending debounce timer in its cleanup before it ever fired — the timer
  // must outlive individual renders.
  const scheduleRef = useRef<(values: unknown) => void>(() => undefined);
  scheduleRef.current = (values: unknown) => {
    const filtered = omitKeys((values ?? {}) as Record<string, unknown>, excludeList) as Partial<T>;
    latestValuesRef.current = filtered;

    if (isBlank(filtered as Record<string, unknown>)) return;

    setStatus('saving');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      persist(filtered, !isOnlineRef.current);
    }, debounceMs);
  };

  // Debounced write on every change. A debounce rather than a fixed interval:
  // no wasted writes while the form sits idle, and no gap where fast typing
  // outruns the timer.
  useEffect(() => {
    if (!enabled) return;

    const subscription = form.watch(values => scheduleRef.current(values));

    return () => {
      subscription.unsubscribe();
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // `form` is intentionally omitted: react-hook-form returns a new object
    // identity on every render, and re-running this effect would cancel the
    // in-flight debounce. The watch subscription itself is stable per form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Write immediately when the tab is hidden or the page is being torn down.
  // `visibilitychange` and `pagehide` fire reliably on mobile Safari, where
  // `beforeunload` does not.
  useEffect(() => {
    if (!enabled) return;

    const handleHide = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    const handlePageHide = () => flush();

    document.addEventListener('visibilitychange', handleHide);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      document.removeEventListener('visibilitychange', handleHide);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [enabled, flush]);

  // Going offline: snapshot right away and mark the draft as holding changes
  // that never reached the server.
  useEffect(() => {
    if (!enabled || isOnline) return;
    setStatus('offline');
    flush({ pendingSync: true });
  }, [enabled, isOnline, flush]);

  const restoreDraft = useCallback(() => {
    if (!pendingDraft) return;
    // keepDefaultValues so fields excluded from the draft (server-owned flags)
    // retain the values the page already loaded.
    form.reset({ ...(form.getValues() as object), ...(pendingDraft.values as object) } as T, {
      keepDefaultValues: true,
    });
    setPendingDraft(null);
    setStatus(pendingDraft.pendingSync ? 'offline' : 'saved');
  }, [pendingDraft, form]);

  const discardDraft = useCallback(() => {
    removeDraft(storageKey, storage);
    setPendingDraft(null);
    setLastSavedAt(null);
    setStatus('idle');
  }, [storageKey, storage]);

  const clearDraft = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    removeDraft(storageKey, storage);
    setPendingDraft(null);
    setLastSavedAt(null);
    setStatus('idle');
  }, [storageKey, storage]);

  return {
    status,
    lastSavedAt,
    pendingDraft,
    restoreDraft,
    discardDraft,
    clearDraft,
    flush,
    isOnline,
    wasOffline,
    acknowledgeReconnect,
  };
}

export default useAutosave;
